type FormatOpts = {
  trimTrailingZeros?: boolean; // default true
  maxDecimals?: number; // default 3
  thousandsSep?: boolean; // default true
};

const WEI_DIVISOR = 1_000_000_000_000_000_000n; // 1e18

/**
 * Formats a bigint (wei), number, or string for display in staking UI.
 * - bigint inputs are divided by 1e18 (treated as wei)
 * - number inputs are treated as human-scale values
 * - string inputs are parsed via parseFloat
 * Always returns Latin (ASCII 0-9) digits. NaN/undefined → "0".
 */
export function formatStakeNumber(
  value: bigint | number | string,
  opts?: FormatOpts,
): string {
  const trimZeros = opts?.trimTrailingZeros ?? true;
  const maxDec = opts?.maxDecimals ?? 3;
  const thousands = opts?.thousandsSep ?? true;

  let humanValue: number;

  if (typeof value === "bigint") {
    // Divide bigint by 1e18 using integer arithmetic to avoid float precision loss
    const wholePart = value / WEI_DIVISOR;
    const fracPart = value % WEI_DIVISOR;
    humanValue = Number(wholePart) + Number(fracPart) / 1e18;
  } else if (typeof value === "number") {
    humanValue = value;
  } else {
    humanValue = Number.parseFloat(value);
  }

  if (Number.isNaN(humanValue)) return "0";

  // Format with maxDecimals
  let formatted = humanValue.toFixed(maxDec);

  if (trimZeros && formatted.includes(".")) {
    formatted = formatted.replace(/\.?0+$/, "");
  }

  // Add thousands separator
  if (thousands) {
    const parts = formatted.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    formatted = parts.join(".");
  }

  return formatted;
}
